// Live-mfe dev only (DESKTOP_DEV). A branch switch or a config edit restarts
// the MFE dev server; the shell's module fetch fails, single-spa parks the app
// in LOAD_ERROR, and the browser caches that failed fetch for the document, so
// only a page reload recovers. This reloads once the entry is served again,
// paced so a broken server cannot turn the desktop into a reload loop.

const STORAGE_KEY = "mfeDevReloads";
export const RELOAD_MAX = 5;
export const RELOAD_WINDOW_MS = 180_000;
const PROBE_DEADLINE_MS = 60_000;
const BACKOFF_BASE_MS = 1_500;
const BACKOFF_MAX_MS = 24_000;

let inFlight = false;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function readAttempts(now: number): number[] {
  const raw = sessionStorage.getItem(STORAGE_KEY);
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (t): t is number => typeof t === "number" && now - t < RELOAD_WINDOW_MS,
    );
  } catch {
    return [];
  }
}

/**
 * Records a reload attempt against the rolling budget and returns its index
 * within the current window, or `null` when the budget is exhausted.
 */
export function takeReloadAttempt(now = Date.now()): number | null {
  const attempts = readAttempts(now);
  if (attempts.length >= RELOAD_MAX) return null;
  attempts.push(now);
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(attempts));
  return attempts.length - 1;
}

async function entryServed(entryUrl: string): Promise<boolean> {
  try {
    const res = await fetch(entryUrl, { cache: "no-store" });
    return res.ok;
  } catch {
    return false;
  }
}

export async function recoverDevMfe(entryUrl: string): Promise<void> {
  if (inFlight) return;
  inFlight = true;
  const attempt = takeReloadAttempt();
  if (attempt === null) {
    console.error(
      `[shell] MFE dev recovery: reload budget exhausted (${RELOAD_MAX} per ${RELOAD_WINDOW_MS / 1000}s); use Reload (keep session) once the dev server is up`,
    );
    return;
  }
  const backoff = Math.min(BACKOFF_BASE_MS << attempt, BACKOFF_MAX_MS);
  console.info(
    `[shell] MFE dev recovery: reload attempt ${attempt + 1} in ${backoff}ms once ${entryUrl} is served`,
  );
  await delay(backoff);
  const deadline = Date.now() + PROBE_DEADLINE_MS;
  while (!(await entryServed(entryUrl))) {
    if (Date.now() >= deadline) {
      console.error(
        `[shell] MFE dev recovery: ${entryUrl} still not served after ${PROBE_DEADLINE_MS / 1000}s; giving up`,
      );
      inFlight = false;
      return;
    }
    await delay(BACKOFF_BASE_MS);
  }
  window.location.reload();
}
